import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImplementerCandidates, useUpdateBugEvaluation } from '../../../hooks/useBugEvaluation';
import type { BugEvaluationDetail } from '../../../lib/bug-evaluation-api';
import {
  ANTI_PATTERN_OPTIONS,
  BUG_TYPES,
  DEV_FLOW_OPTIONS,
  EN_LABELS,
  IRREGULAR_OPTIONS,
  NEGLIGENCE_LEVELS,
  RECURRENCE_OPTIONS,
} from './constants';
import { ImplementerCandidates } from './ImplementerCandidates';
import { SelectField } from './SelectField';

interface ReviewFormProps {
  issueKey: string;
  detail: BugEvaluationDetail;
  jaDetail: BugEvaluationDetail | undefined;
  currentLocale: 'ja' | 'en';
  canEdit: boolean;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価レビューフォーム。編集モードの開閉と各項目（bugType / negligenceLevel / iPoint 等）の編集 UI を保持する。Firestore は日本語値で正規化されているため、編集開始時には ja データを seed として使う。
 * @graph-connects SelectField [calls] セレクトフィールド描画
 * @graph-connects ImplementerCandidates [calls] 実装担当者の候補の描画
 * @graph-connects useImplementerCandidates [calls] 実装担当者の候補取得
 */
export function ReviewForm({
  issueKey,
  detail,
  jaDetail,
  currentLocale,
  canEdit,
}: ReviewFormProps) {
  const { t } = useTranslation();
  const updateMutation = useUpdateBugEvaluation(issueKey);
  const [editMode, setEditMode] = useState(false);
  // 候補は確定する人だけが見る情報なので、編集モードに入ってから引く
  const { data: candidatesData } = useImplementerCandidates(issueKey, editMode && canEdit);
  const [formData, setFormData] = useState<Record<string, string | number>>({});

  const startEdit = () => {
    const jaSource = jaDetail ?? detail;
    setFormData({
      bugType: jaSource.bugType,
      negligenceLevel: jaSource.negligenceLevel,
      iPoint: jaSource.iPoint,
      recurrence: jaSource.recurrence,
      antiPattern: jaSource.antiPattern,
      irregular: jaSource.irregular,
      devFlowCompliance: jaSource.devFlowCompliance,
      evaluationReason: detail.evaluationReason,
      // 未確定は空文字で持つ（選択肢の先頭が「未確定」）
      implementer: detail.implementer ?? '',
    });
    setEditMode(true);
  };

  const handleSave = () => {
    // evaluationReasonが実際に変更されたかチェック（翻訳済みテキストでFirestoreを上書きしない）
    const originalReason = detail.evaluationReason;
    const newReason = formData.evaluationReason as string;
    const reasonChanged = newReason !== originalReason;
    // 変更がないときは送らない。毎回送ると、選択肢が未取得のまま保存した場合に
    // 既存の実装担当者を空文字で消してしまう
    const implementer = String(formData.implementer ?? '');
    const implementerChanged = implementer !== (detail.implementer ?? '');

    updateMutation.mutate(
      {
        bugType: formData.bugType as string,
        negligenceLevel: formData.negligenceLevel as string,
        // APIのiPointスキーマは整数のみ受け付ける（.int()）。number inputは小数を
        // 入力できるため、丸めずに送ると400で保存が失敗する
        iPoint: Math.min(10, Math.max(0, Math.round(Number(formData.iPoint)))),
        recurrence: formData.recurrence as string,
        antiPattern: formData.antiPattern as string,
        irregular: formData.irregular as string,
        devFlowCompliance: formData.devFlowCompliance as string,
        ...(reasonChanged ? { evaluationReason: newReason, locale: currentLocale } : {}),
        ...(implementerChanged ? { implementer } : {}),
      },
      {
        onSuccess: () => {
          setEditMode(false);
        },
      },
    );
  };

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
          {t('bugEvaluation.review', 'レビュー')}
        </h2>
        {!editMode && canEdit && (
          <Button variant="secondary" onClick={startEdit}>
            {t('bugEvaluation.edit', '編集')}
          </Button>
        )}
      </div>

      {editMode ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            <SelectField
              label={t('bugEvaluation.bugType', 'バグ種別')}
              value={String(formData.bugType ?? '')}
              options={BUG_TYPES}
              labelMap={currentLocale !== 'ja' ? EN_LABELS.bugType : undefined}
              onChange={(v) => {
                setFormData({ ...formData, bugType: v });
              }}
            />
            <SelectField
              label={t('bugEvaluation.negligenceLevel', '過失レベル')}
              value={String(formData.negligenceLevel ?? '')}
              options={NEGLIGENCE_LEVELS}
              labelMap={currentLocale !== 'ja' ? EN_LABELS.negligenceLevel : undefined}
              onChange={(v) => {
                setFormData({ ...formData, negligenceLevel: v });
              }}
            />
            <div>
              <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
                i-point (0-10)
              </label>
              <input
                type="number"
                min={0}
                max={10}
                step={1}
                value={formData.iPoint ?? 0}
                onChange={(e) => {
                  setFormData({ ...formData, iPoint: Number(e.target.value) });
                }}
                className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5 text-sm"
              />
            </div>
            <SelectField
              label={t('bugEvaluation.recurrence', '再発当否')}
              value={String(formData.recurrence ?? '')}
              options={RECURRENCE_OPTIONS}
              labelMap={currentLocale !== 'ja' ? EN_LABELS.recurrence : undefined}
              onChange={(v) => {
                setFormData({ ...formData, recurrence: v });
              }}
            />
            <SelectField
              label={t('bugEvaluation.antiPattern', 'アンチパターン')}
              value={String(formData.antiPattern ?? '')}
              options={ANTI_PATTERN_OPTIONS}
              labelMap={currentLocale !== 'ja' ? EN_LABELS.antiPattern : undefined}
              onChange={(v) => {
                setFormData({ ...formData, antiPattern: v });
              }}
            />
            <SelectField
              label={t('bugEvaluation.irregular', 'イレギュラー')}
              value={String(formData.irregular ?? '')}
              options={IRREGULAR_OPTIONS}
              labelMap={currentLocale !== 'ja' ? EN_LABELS.irregular : undefined}
              onChange={(v) => {
                setFormData({ ...formData, irregular: v });
              }}
            />
            <SelectField
              label={t('bugEvaluation.flowCompliance', '開発フロー準拠')}
              value={String(formData.devFlowCompliance ?? '')}
              options={DEV_FLOW_OPTIONS}
              labelMap={currentLocale !== 'ja' ? EN_LABELS.devFlowCompliance : undefined}
              onChange={(v) => {
                setFormData({ ...formData, devFlowCompliance: v });
              }}
            />
          </div>
          <div className="space-y-2">
            <SelectField
              label={t('bugEvaluation.implementer', '実装担当者（起因案件）')}
              value={String(formData.implementer ?? '')}
              // 選択肢は API が返したものだけを使う。画面に在籍者を写さない
              options={['', ...(detail.implementerOptions ?? [])]}
              labelMap={{ '': t('bugEvaluation.implementerUnset', '未確定') }}
              onChange={(v) => {
                setFormData({ ...formData, implementer: v });
              }}
            />
            {candidatesData?.data && (
              <ImplementerCandidates
                causeKey={candidatesData.data.causeKey}
                candidates={candidatesData.data.candidates}
                selected={String(formData.implementer ?? '')}
                onPick={(nickName) => {
                  setFormData({ ...formData, implementer: nickName });
                }}
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
              {t('bugEvaluation.evaluationReason', '評価理由')}
            </label>
            <textarea
              value={String(formData.evaluationReason ?? '')}
              onChange={(e) => {
                setFormData({ ...formData, evaluationReason: e.target.value });
              }}
              rows={4}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            />
          </div>
          {updateMutation.isError && (
            <div className="p-3 rounded-lg bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 text-sm text-[var(--color-error)]">
              {t(
                'bugEvaluation.saveFailed',
                'レビューの保存に失敗しました。時間をおいて再度お試しください。',
              )}
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="primary" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending
                ? t('bugEvaluation.saving', '保存中...')
                : t('bugEvaluation.saveReview', 'レビュー保存')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setEditMode(false);
              }}
            >
              {t('bugEvaluation.cancel', 'キャンセル')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[var(--color-text-muted)]">
          {canEdit
            ? t(
                'bugEvaluation.editHint',
                '評価結果に修正がある場合は「編集」をクリックしてください。保存するとFirestore経由でBQ・スプレッドシートに自動反映されます。',
              )
            : t(
                'bugEvaluation.noEditPermission',
                'レビュー編集はArchitect・Tech Lead・Adminのみ実行できます。',
              )}
        </p>
      )}
    </Card>
  );
}
